<template>
  <div class="security-settings-page">
    <div class="page-header">
      <div>
        <p class="eyebrow">SECURITY</p>
        <h1>{{ screenName }}</h1>
        <p>
          ログインの方法を増やしたり、入っている端末を切ったりできます。
          パスワードだけでも使えますが、<b>2要素認証かパスキーを足すと、
          パスワードが漏れても入られなくなります</b>。
        </p>
      </div>
    </div>

    <CAlert
      v-if="message"
      color="success"
      close-button
      @update:show="message = ''"
    >{{ message }}</CAlert>

    <CAlert
      v-if="errorMessage"
      color="danger"
      close-button
      @update:show="errorMessage = ''"
    >{{ errorMessage }}</CAlert>

    <!--
      ログインの方法を先に、端末の一覧を後に置く。

      **利用者がここへ来る理由の多くは「2要素を付けたい」。**端末の一覧を
      先に出すと、目的のものが画面の下にあって見つけられない。
    -->
    <MfaSettings/>

    <PasskeySettings/>

    <ExternalAccountSettings/>

    <div v-if="loading" class="security-settings-loading">
      <CSpinner color="primary"/>
      <span>入っている端末を読み込んでいます。</span>
    </div>

    <CCard v-else class="settings-card">
      <CCardHeader>
        <div class="section-heading">
          <span class="section-icon"><CIcon name="cil-devices"/></span>
          <div>
            <strong>いま入っている端末</strong>
            <p>
              最後に使った順です。<b>この端末</b>と付いているものが、
              いま操作している{{ productName }}です。
            </p>
          </div>
        </div>
      </CCardHeader>

      <CCardBody>
        <p v-if="!sessions.length" class="security-empty">
          入っている端末がありません。
        </p>

        <ul v-else class="security-session-list">
          <li
            v-for="item in sessions"
            :key="item.id"
            class="security-session"
            :class="{ 'security-session--current': item.current }"
          >
            <div class="security-session__body">
              <p class="security-session__title">
                {{ describeDevice(item) }}
                <span v-if="item.current" class="security-session__badge">
                  この端末
                </span>
              </p>
              <p class="security-session__detail">
                最後に使ったの: {{ formatTime(item.lastSeenAt) }}
              </p>
              <p class="security-session__detail">
                入った日時: {{ formatTime(item.createdAt) }}
                ／ 入り方: {{ describeMethod(item.authMethod) }}
                ／ 接続元: {{ item.ipAddress || '不明' }}
              </p>
            </div>

            <!--
              いま使っている端末には、切るボタンを出さない。

              押せてしまうと、自分をその場で締め出す。「全部から出る」は
              ログアウトでできるので、ここでは出さない。
            -->
            <CButton
              v-if="!item.current"
              color="outline-danger"
              size="sm"
              :disabled="busy"
              @click="revoke(item)"
            >この端末を切る</CButton>
          </li>
        </ul>
      </CCardBody>

      <CCardFooter v-if="otherCount > 0">
        <!--
          いまの端末は残す。押した本人がその場で締め出されると、
          「心当たりのない端末がある」ときに何もできなくなる。
        -->
        <CButton
          color="outline-danger"
          :disabled="busy"
          @click="revokeOthers"
        >ほかの{{ otherCount }}台を全部切る</CButton>
        <p class="security-settings-note">
          いま操作しているこの端末は残ります。
        </p>
      </CCardFooter>
    </CCard>
  </div>
</template>

<script>
import {
  listSessions,
  revokeSession,
  revokeOtherSessions,
  isReauthenticationRequired,
  isNetworkFailure
} from '../../services/authSessions'
import { SECURITY_SCREEN_NAME } from '../../common/screenNames'
import { PRODUCT_NAME } from '../../common/productName'
import MfaSettings from './MfaSettings'
import PasskeySettings from './PasskeySettings'
import ExternalAccountSettings from './ExternalAccountSettings'

/**
 * 入り方の見せ方。
 *
 * **知らない値はそのまま出さない。** サーバが増やした値をそのまま画面へ
 * 出すと、表示が英大文字のまま混ざる。増えたら気づけるよう、既定は
 * 「その他」にしておく。
 */
const METHOD_LABELS = {
  PASSWORD: 'メールアドレスとパスワード',
  PASSKEY: 'パスキー',
  MICROSOFT: 'Microsoft アカウント',
  GOOGLE: 'Google アカウント',
  ENTERPRISE_SSO: '会社のログイン'
}

export default {
  name: 'SecuritySettings',

  components: {
    MfaSettings,
    PasskeySettings,
    ExternalAccountSettings
  },

  data () {
    return {
      sessions: [],
      loading: true,
      busy: false,
      message: '',
      errorMessage: ''
    }
  },

  computed: {
    /** 売るときの正式な表記。**1か所から取る。** */
    productName () {
      return PRODUCT_NAME
    },

    /** 見出し。**右上のメニューに出る名前と同じもの。** */
    screenName () {
      return SECURITY_SCREEN_NAME
    },

    /** いまの端末を除いた台数。 */
    otherCount () {
      return this.sessions.filter(item => !item.current).length
    }
  },

  mounted () {
    this.reload()
  },

  methods: {
    async reload () {
      this.loading = true
      try {
        this.sessions = await listSessions()
      } catch (error) {
        this.sessions = []
        this.errorMessage = this.explain(error,
          '入っている端末を読み込めませんでした。')
      } finally {
        this.loading = false
      }
    },

    async revoke (item) {
      if (this.busy) return

      this.busy = true
      this.message = ''
      this.errorMessage = ''
      try {
        const revoked = await revokeSession(item.id)
        /*
         * サーバは「切れたかどうか」しか返さない。切れなかったときに
         * 「切れました」と出すと、利用者は盗まれた端末を切ったつもりで
         * 放置する。**切れていないことは、そのまま伝える。**
         */
        this.message = revoked
          ? 'この端末を切りました。'
          : ''
        if (!revoked) {
          this.errorMessage = 'この端末は切れませんでした。'
            + ' 画面を開き直してからもう一度お試しください。'
        }
        await this.reload()
      } catch (error) {
        this.errorMessage = this.explain(error, '端末を切れませんでした。')
      } finally {
        this.busy = false
      }
    },

    async revokeOthers () {
      if (this.busy) return

      this.busy = true
      this.message = ''
      this.errorMessage = ''
      try {
        const revoked = await revokeOtherSessions()
        this.message = revoked > 0
          ? 'ほかの' + revoked + '台を切りました。'
          : '切る端末がありませんでした。'
        await this.reload()
      } catch (error) {
        this.errorMessage = this.explain(error, '端末を切れませんでした。')
      } finally {
        this.busy = false
      }
    },

    /**
     * 断られた理由の見せ方。
     *
     * **本人確認のやり直しと、通信が届かないことだけを分ける。**
     * それ以外は1種類にまとめる。細かく分けると、応答の違いから
     * 外の人が中の状態を推せる。
     */
    explain (error, fallback) {
      if (isReauthenticationRequired(error)) {
        return 'もう一度ログインの確認が必要です。'
          + ' 一度ログアウトして入り直してから、もう一度お試しください。'
      }
      if (isNetworkFailure(error)) {
        return '通信できませんでした。接続を確認してからお試しください。'
      }
      return fallback
    },

    describeMethod (authMethod) {
      return METHOD_LABELS[authMethod] || 'その他'
    },

    /**
     * 端末の見せ方。
     *
     * **ユーザーエージェントをそのまま出さない。** 長くて読めないうえ、
     * 画面に出た文字列がそのまま他の場所へ貼られる。読める形に短くする。
     */
    describeDevice (item) {
      const agent = String(item.userAgent || '')
      if (!agent) return '不明な端末'

      const os = ['Windows', 'Mac', 'iPhone', 'iPad', 'Android', 'Linux']
        .find(name => agent.indexOf(name) >= 0)
      const browser = ['Edg', 'Chrome', 'Firefox', 'Safari']
        .find(name => agent.indexOf(name) >= 0)

      if (!os && !browser) return '不明な端末'
      return [os || '不明なOS', browser === 'Edg' ? 'Edge' : browser]
        .filter(Boolean).join(' / ')
    },

    formatTime (value) {
      if (!value) return '不明'
      const at = new Date(value)
      if (isNaN(at.getTime())) return '不明'
      return at.toLocaleString('ja-JP')
    }
  }
}
</script>
